import React from 'react';
import SmartImage from './SmartImage';
import Link from './Link';
import { Icons } from './Icons';
import './CategoryTile.css';

/**
 * Column counts for a category row.
 *
 * `auto-fill` cannot be used here. It creates as many tracks as the
 * container can hold, so five categories in a wide container fill five
 * tracks and leave the rest empty — the row then hugs the left edge
 * instead of spanning it evenly. Driving the count from the real number
 * of items keeps the row symmetrical whether there are three categories
 * or nine, and the per-breakpoint caps stop tiles growing absurdly wide
 * when there are only two.
 *
 * @param {number} count how many tiles will be rendered
 * @returns {React.CSSProperties} custom properties consumed by .cat-grid
 */
export function catGridStyle(count) {
  const n = Math.max(1, count || 1);
  return {
    '--cat-cols': Math.min(n, 5),      // desktop
    '--cat-cols-md': Math.min(n, 4),   // tablet
    '--cat-cols-sm': Math.min(n, 3),   // phone
  };
}

/**
 * One tile in a category or subcategory grid.
 *
 * Used by both the home page and the shop so the two never drift
 * apart visually. The cover photo is a real product photo pulled
 * from the category — nothing is hardcoded and nothing is invented
 * when a category has no products yet; it falls back to an icon.
 *
 * @param {string} name
 * @param {string} [image]  cover photo URL
 * @param {string} [meta]   small line under the name, e.g. a count
 * @param {Function} onClick
 * @param {Function} [icon] icon renderer used when there is no image
 * @param {boolean} [active]
 */
export default function CategoryTile({ name, image, meta, onClick, icon, href, active = false }) {
  const renderIcon = icon || Icons.diamond;

  return (
    <Link
      href={href || '/shop'}
      className={`cat-tile${active ? ' is-active' : ''}`}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
    >
      <SmartImage
        src={image}
        alt={`${name} collection`}
        className="cat-tile__img"
        sizes="(max-width: 767px) 33vw, (max-width: 1024px) 22vw, 220px"
        fallback={<span className="cat-tile__icon">{renderIcon(28)}</span>}
      />
      <span className="cat-tile__text">
        <span className="cat-tile__name">{name}</span>
        {meta && <span className="cat-tile__meta">{meta}</span>}
      </span>
    </Link>
  );
}
